import React from 'react';
import { EventFormEditor } from '../../presentational/EventFormEditor';
import { EventFormValues } from '../../presentational/hooks/useEventFormState';
import { FormType } from '@graphql/generated';
import { EventFormData, FormSettings } from '../shared/types';
import { parseFormSettings } from '../shared/parseFormSettings';
import { useRegistrationPreviousResponse } from './hooks/useRegistrationPreviousResponse';
import { TextV2 } from '@withjoy/joykit';
import { useSaveRegistration } from './hooks/useSaveRegistration';
import { useColorPaletteProvider } from '@apps/guest';

type RegistrationFormProps = {
  formData: EventFormData & { type: FormType.REGISTRATION };
  onComplete?: (values: EventFormValues) => void;
};

export const RegistrationForm = (props: RegistrationFormProps) => {
  const { formData, onComplete } = props;
  const baseSettings = parseFormSettings(formData.settings);
  const { loading, answers, contact, guestKey, attendanceState } = useRegistrationPreviousResponse(formData.id, formData.eventId ?? '');
  const { saveRegistration, isGetSessionLoading } = useSaveRegistration();

  // We have a previous response if:
  // 1. We have a contact (means they submitted the contact form at some point)
  // 2. they have either done attendance (hasAttendanceQuestion) or have answered some questions (!hasAttendanceQuestion).
  // we only prefill data and show guest confirmation step if they have submitted at some point.
  // AND, if they have submitted, we hide address and phone number as those cannot be edited after submission.
  const hasSubmitted = contact !== undefined && (attendanceState !== null || Object.keys(answers).length > 0);

  const settings: FormSettings = hasSubmitted
    ? {
        ...baseSettings,
        addressFields: 'hidden',
        phoneFields: 'hidden',
      }
    : baseSettings;

  const colors = useColorPaletteProvider();

  if (loading || isGetSessionLoading) {
    return <TextV2>Loading...</TextV2>;
  }

  const initialData = hasSubmitted
    ? {
        formValues: answers,
        contactValues: contact,
        attendanceValue: attendanceState,
      }
    : undefined;

  return (
    <EventFormEditor
      isFormDisabled={!formData.enabled}
      formType={formData.type}
      settings={{
        isRegistration: true,
        ...settings,
      }}
      formData={formData.config}
      initialData={initialData}
      guestInfo={{
        showConfirmation: guestKey !== undefined && contact !== undefined,
        guestDisplayName: `${contact?.firstName} ${contact?.lastName}`,
        hasSubmitted,
      }}
      onSubmit={(values: EventFormValues) => {
        saveRegistration({ ...values, formId: formData.id, guestKey, eventId: formData.eventId ?? '' }).then(() => {
          onComplete?.(values);
        });
      }}
      colors={colors}
    />
  );
};
